import type { Theme } from './types'

/**
 * Generates the full CSS string for a given theme.
 * Mirrors the prototype's generateThemeCSS() — kept as a single template string
 * so it stays trivial to diff against the original Leadflow v4.0.html.
 */
export function generateThemeCSS(theme: Theme): string {
  const { colors, radiusSm, radiusMd, radiusLg, shadow, font, isDark } = theme
  const sendRadius =
    theme.buttonStyle === 'pill' ? '999px' : theme.buttonStyle === 'sharp' ? radiusSm : radiusMd

  return `
    body {
      font-family: ${font};
      background: ${colors.wrapperBg};
      color: ${colors.text};
    }

    .viewport-wrapper { background: ${colors.wrapperBg}; }
    .viewport-container { background: ${colors.bg}; }

    .progress-bar {
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: ${colors.progressBg};
      z-index: 10;
    }
    .progress-fill {
      height: 100%;
      background: ${colors.accent};
      transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .chat-header {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 28px 24px 20px;
      position: relative;
      flex-shrink: 0;
      background: ${colors.bg};
      border-bottom: 1px solid ${colors.border};
    }
    .header-avatar {
      width: 56px; height: 56px;
      border-radius: 50%;
      background: ${colors.accent};
      display: flex; align-items: center; justify-content: center;
      color: ${colors.accentText};
      font-weight: 600; font-size: 20px;
      box-shadow: ${shadow};
      overflow: hidden;
      ${isDark ? 'border: 2px solid ' + colors.border + ';' : ''}
    }
    .header-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .header-text { margin-left: 12px; display: flex; flex-direction: column; }
    .header-name { font-weight: 600; font-size: 15px; color: ${colors.text}; }
    .header-status {
      font-size: 12px;
      color: ${colors.accent};
      display: flex; align-items: center; gap: 6px;
    }
    .header-status::before {
      content: '';
      width: 6px; height: 6px;
      border-radius: 50%;
      background: ${colors.accent};
      display: inline-block;
    }

    .chat-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      scroll-behavior: smooth;
      padding: 20px 20px 8px;
      display: flex; flex-direction: column; gap: 14px;
    }
    .chat-scroll::-webkit-scrollbar { width: 0; }

    .msg-bot {
      display: flex; gap: 10px; align-items: flex-end;
      animation: msgIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .msg-bot-avatar {
      width: 32px; height: 32px;
      border-radius: 50%;
      background: ${colors.accent};
      overflow: hidden;
      flex-shrink: 0;
      ${isDark ? 'border: 1px solid ' + colors.border + ';' : ''}
    }
    .msg-bot-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .msg-bot-bubble {
      background: ${colors.bgSubtle};
      color: ${colors.text};
      padding: 12px 16px;
      border-radius: ${radiusLg};
      border-bottom-left-radius: ${radiusSm};
      font-size: 16px; line-height: 1.45;
      max-width: 78%;
      ${isDark ? 'border: 1px solid ' + colors.border + ';' : ''}
      word-wrap: break-word;
    }

    .msg-user {
      display: flex; justify-content: flex-end;
      animation: msgInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .msg-user-bubble {
      background: ${colors.accent};
      color: ${colors.accentText};
      padding: 12px 16px;
      border-radius: ${radiusLg};
      border-bottom-right-radius: ${radiusSm};
      font-size: 16px; line-height: 1.45;
      max-width: 78%;
      font-weight: 500;
      word-wrap: break-word;
      white-space: pre-line;
    }

    .msg-typing { display: flex; gap: 10px; align-items: flex-end; animation: msgIn 0.2s ease; }
    .typing-bubble {
      background: ${colors.bgSubtle};
      padding: 14px 16px;
      border-radius: ${radiusLg};
      border-bottom-left-radius: ${radiusSm};
      display: flex; gap: 4px;
      ${isDark ? 'border: 1px solid ' + colors.border + ';' : ''}
    }
    .typing-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: ${colors.textTertiary};
      animation: bounce 1.4s infinite;
    }
    .typing-dot:nth-child(2) { animation-delay: 0.15s; }
    .typing-dot:nth-child(3) { animation-delay: 0.3s; }

    @keyframes bounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
      30% { transform: translateY(-5px); opacity: 1; }
    }
    @keyframes msgIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes msgInRight {
      from { opacity: 0; transform: translateX(8px); }
      to { opacity: 1; transform: translateX(0); }
    }

    /* ─── Inline CTA-knap (fx welcome "Start") ────────── */
    .msg-cta {
      animation: msgIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
      padding: 8px 0 4px;
    }

    /* ─── Savings reveal card ─────────────────────────── */
    .msg-savings {
      animation: msgIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
      padding: 6px 0;
    }
    .savings-card {
      position: relative;
      background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover ?? colors.accent} 100%);
      color: #fff;
      border-radius: 20px;
      padding: 22px 22px 20px;
      box-shadow: 0 18px 40px -12px ${colors.accent}66, 0 4px 12px rgba(0,0,0,.08);
      overflow: hidden;
      animation: savings-pop 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    .savings-card::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at 20% 0%, rgba(255,255,255,0.18), transparent 60%);
      pointer-events: none;
    }
    @keyframes savings-pop {
      from { opacity: 0; transform: translateY(14px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    .savings-eyebrow {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      opacity: 0.9;
      margin-bottom: 8px;
    }
    .savings-amount {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px 8px;
      line-height: 1;
      font-feature-settings: 'tnum';
      font-variant-numeric: tabular-nums;
    }
    .savings-num {
      font-size: 36px;
      font-weight: 800;
      letter-spacing: -0.02em;
    }
    .savings-dash {
      font-size: 28px;
      font-weight: 600;
      opacity: 0.7;
    }
    .savings-unit {
      font-size: 18px;
      font-weight: 600;
      margin-left: 4px;
      opacity: 0.95;
    }
    .savings-period {
      font-size: 14px;
      font-weight: 500;
      margin-top: 6px;
      opacity: 0.92;
    }
    .savings-bar {
      margin-top: 14px;
      height: 6px;
      background: rgba(255,255,255,0.25);
      border-radius: 999px;
      overflow: hidden;
    }
    .savings-bar-fill {
      height: 100%;
      width: 0;
      background: rgba(255,255,255,0.85);
      border-radius: 999px;
      animation: savings-bar 1.1s 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    @keyframes savings-bar {
      to { width: 100%; }
    }
    .savings-reframe {
      margin-top: 14px;
      font-size: 13px;
      font-weight: 500;
      opacity: 0.95;
    }
    @media (prefers-reduced-motion: reduce) {
      .savings-card, .msg-savings { animation: none !important; }
      .savings-bar-fill { animation: none !important; width: 100% !important; }
    }

    .input-dock {
      flex-shrink: 0;
      padding: 12px 20px 20px;
      background: ${colors.bg};
      border-top: 1px solid ${colors.border};
      display: flex; flex-direction: column; gap: 10px;
      animation: dock-in 0.42s cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    @keyframes dock-in {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    /* Children get a small stagger so the dock unfolds rather than slamming */
    .input-dock > * {
      animation: dock-child-in 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    .input-dock > *:nth-child(1) { animation-delay: 0.04s; }
    .input-dock > *:nth-child(2) { animation-delay: 0.10s; }
    .input-dock > *:nth-child(3) { animation-delay: 0.16s; }
    .input-dock > *:nth-child(4) { animation-delay: 0.22s; }
    .input-dock > *:nth-child(5) { animation-delay: 0.28s; }
    @keyframes dock-child-in {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    /* Insurance + chip cards get an even finer-grained stagger inside the grid */
    .insurance-grid .insurance-option,
    .chip-grid .chip-option {
      animation: card-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    .insurance-grid .insurance-option:nth-child(1),
    .chip-grid .chip-option:nth-child(1) { animation-delay: 0.10s; }
    .insurance-grid .insurance-option:nth-child(2),
    .chip-grid .chip-option:nth-child(2) { animation-delay: 0.14s; }
    .insurance-grid .insurance-option:nth-child(3),
    .chip-grid .chip-option:nth-child(3) { animation-delay: 0.18s; }
    .insurance-grid .insurance-option:nth-child(4),
    .chip-grid .chip-option:nth-child(4) { animation-delay: 0.22s; }
    .insurance-grid .insurance-option:nth-child(5),
    .chip-grid .chip-option:nth-child(5) { animation-delay: 0.26s; }
    .insurance-grid .insurance-option:nth-child(6),
    .chip-grid .chip-option:nth-child(6) { animation-delay: 0.30s; }
    @keyframes card-in {
      from { opacity: 0; transform: translateY(10px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    /* Respect users who prefer no motion */
    @media (prefers-reduced-motion: reduce) {
      .input-dock,
      .input-dock > *,
      .insurance-grid .insurance-option,
      .chip-grid .chip-option {
        animation: none !important;
      }
    }
    .input-row { display: flex; gap: 8px; align-items: stretch; }

    .input-field {
      width: 100%;
      padding: 14px 16px;
      border: 1.5px solid ${colors.border};
      border-radius: ${radiusMd};
      font-family: ${font};
      font-size: 16px;
      color: ${colors.text};
      background: ${colors.inputBg};
      transition: all 0.2s ease;
    }
    .input-field:focus {
      outline: none;
      border-color: ${colors.accent};
      box-shadow: 0 0 0 3px ${colors.accentSoft};
    }
    .input-field::placeholder { color: ${colors.textTertiary}; }

    .btn-send {
      padding: 14px 22px;
      background: ${colors.disabledBg};
      color: ${colors.disabledText};
      border: none;
      border-radius: ${sendRadius};
      font-family: ${font};
      font-size: 15px; font-weight: 600;
      cursor: not-allowed;
      transition: all 0.2s ease;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .btn-send.full { width: 100%; padding: 14px; font-size: 16px; }
    .btn-send:not(:disabled) {
      background: ${colors.accent};
      color: ${colors.accentText};
      cursor: pointer;
    }
    .btn-send:not(:disabled):hover {
      background: ${colors.accentHover};
      transform: translateY(-1px);
    }
    .btn-send:not(:disabled):active { transform: translateY(0); }

    /* ─── Phone step: hero input ────────────────────────── */
    .phone-dock { gap: 12px; padding-bottom: 22px; }
    .phone-trust {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 6px 8px;
      font-size: 12px;
      font-weight: 600;
      color: ${colors.textSecondary};
      letter-spacing: 0.1px;
    }
    .phone-trust-item { white-space: nowrap; }
    .phone-trust-dot { color: ${colors.textTertiary}; opacity: 0.6; }

    .phone-field {
      display: flex;
      align-items: stretch;
      border: 2px solid ${colors.accent};
      border-radius: ${radiusMd};
      background: ${colors.inputBg};
      box-shadow: 0 0 0 4px ${colors.accentSoft};
      transition: box-shadow 0.2s ease, border-color 0.2s ease;
      animation: phone-pulse 1.4s 0.2s cubic-bezier(0.4, 0, 0.6, 1) 1;
    }
    .phone-field:focus-within {
      box-shadow: 0 0 0 5px ${colors.accentSoft};
    }
    .phone-field-error {
      border-color: #ef4444;
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
    }
    .phone-prefix {
      display: flex;
      align-items: center;
      padding: 0 14px;
      font-size: 15px;
      font-weight: 600;
      color: ${colors.text};
      background: ${colors.bgSubtle};
      border-right: 1px solid ${colors.border};
      border-radius: calc(${radiusMd} - 2px) 0 0 calc(${radiusMd} - 2px);
      white-space: nowrap;
      letter-spacing: 0.2px;
    }
    .phone-input {
      flex: 1;
      min-width: 0;
      padding: 16px 14px;
      border: none;
      outline: none;
      background: transparent;
      font-family: ${font};
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 0.5px;
      color: ${colors.text};
      font-feature-settings: 'tnum';
      font-variant-numeric: tabular-nums;
    }
    .phone-input::placeholder {
      color: ${colors.textTertiary};
      font-weight: 500;
      letter-spacing: 0.3px;
    }
    .phone-error {
      font-size: 13px;
      font-weight: 500;
      color: #ef4444;
      text-align: center;
      margin: -2px 0 0;
      animation: msgIn 0.25s ease;
    }
    .btn-send-cta {
      font-size: 17px !important;
      font-weight: 700 !important;
      letter-spacing: 0.2px;
      padding: 16px !important;
      box-shadow: 0 8px 20px -8px ${colors.accent}99;
    }
    .btn-send-cta:not(:disabled):hover {
      box-shadow: 0 12px 26px -8px ${colors.accent};
      transform: translateY(-2px);
    }
    @keyframes phone-pulse {
      0%   { box-shadow: 0 0 0 4px ${colors.accentSoft}; }
      50%  { box-shadow: 0 0 0 8px ${colors.accentSoft}; }
      100% { box-shadow: 0 0 0 4px ${colors.accentSoft}; }
    }
    @media (prefers-reduced-motion: reduce) {
      .phone-field { animation: none !important; }
      .btn-send-cta:not(:disabled):hover { transform: none; }
    }

    .slider-display { text-align: center; padding: 6px 0 10px; }
    .slider-value {
      font-size: 36px; font-weight: 700;
      color: ${colors.accent};
      line-height: 1.1; letter-spacing: -0.02em;
    }
    .slider-suffix { font-size: 13px; color: ${colors.textSecondary}; margin-top: 2px; }
    .slider-track {
      -webkit-appearance: none; appearance: none;
      width: 100%; height: 6px;
      background: ${colors.border};
      border-radius: 999px;
      outline: none; cursor: pointer;
    }
    .slider-track::-webkit-slider-thumb {
      -webkit-appearance: none; appearance: none;
      width: 24px; height: 24px;
      background: ${colors.accent};
      border: 3px solid ${colors.bg};
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      transition: transform 0.15s ease;
    }
    .slider-track::-webkit-slider-thumb:hover { transform: scale(1.1); }
    .slider-track::-moz-range-thumb {
      width: 24px; height: 24px;
      background: ${colors.accent};
      border: 3px solid ${colors.bg};
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    }
    .slider-bounds {
      display: flex; justify-content: space-between;
      font-size: 12px; color: ${colors.textSecondary};
      margin-top: 6px; padding: 0 2px;
    }
    .slider-link {
      background: transparent; border: none;
      color: ${colors.textSecondary};
      opacity: 0.8; font-size: 14px;
      padding: 4px; cursor: pointer;
      font-family: inherit;
    }

    .chip-group { display: flex; flex-direction: column; gap: 8px; }
    .chip-option {
      width: 100%; padding: 14px 16px;
      border: 1.5px solid ${colors.border};
      border-radius: ${radiusMd};
      background: ${colors.inputBg};
      color: ${colors.text};
      font-family: ${font};
      font-size: 15px;
      text-align: left;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .chip-option:hover { border-color: ${colors.accent}; background: ${colors.bgSubtle}; }
    .chip-option.selected {
      border-color: ${colors.accent};
      background: ${colors.accent};
      color: ${colors.accentText};
      font-weight: 600;
    }
    .chip-option.none-row {
      display: flex; align-items: center; gap: 10px;
      justify-content: center; text-align: center;
    }

    .autocomplete-wrapper { position: relative; flex: 1; }
    .autocomplete-suggestions {
      position: absolute;
      bottom: calc(100% + 4px);
      left: 0; right: 0;
      background: ${colors.inputBg};
      border: 1.5px solid ${colors.border};
      border-radius: ${radiusMd};
      max-height: 180px;
      overflow-y: auto;
      opacity: 0;
      transform: translateY(4px);
      pointer-events: none;
      transition: all 0.2s ease;
      box-shadow: ${shadow};
      z-index: 100;
    }
    .autocomplete-suggestions.show { opacity: 1; transform: translateY(0); pointer-events: all; }
    .suggestion-item {
      padding: 12px 16px;
      cursor: pointer;
      display: flex; justify-content: space-between; align-items: center;
      transition: background 0.15s ease;
      border-bottom: 1px solid ${colors.border};
    }
    .suggestion-item:last-child { border-bottom: none; }
    .suggestion-item:hover { background: ${colors.bgSubtle}; }
    .suggestion-item .city { font-weight: 500; color: ${colors.text}; }
    .suggestion-item .postal { font-size: 14px; color: ${colors.textSecondary}; }

    .insurance-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .insurance-option {
      position: relative;
      background: ${colors.inputBg};
      border: 1.5px solid ${colors.border};
      border-radius: ${radiusMd};
      padding: 16px 8px 12px;
      display: flex; flex-direction: column; align-items: center; gap: 8px;
      cursor: pointer;
      transition: all 0.15s ease;
      font-family: ${font};
      color: ${colors.text};
    }
    .insurance-option:hover { border-color: ${colors.accent}; }
    .insurance-option.selected {
      border-color: ${colors.accent};
      background: ${colors.accentSoft};
    }
    .insurance-icon {
      width: 40px; height: 40px;
      color: ${colors.text};
      transition: color 0.15s ease;
    }
    .insurance-option.selected .insurance-icon { color: ${colors.accent}; }
    .insurance-label { font-size: 13px; font-weight: 500; }

    /* Bundle-progress meter under insurance grid */
    .bundle-meter {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      font-size: 13px;
      padding: 8px 12px;
      border-radius: ${radiusMd};
      margin-top: 4px;
      transition: background 0.2s ease, color 0.2s ease;
    }
    .bundle-meter-text { font-weight: 500; }
    .bundle-meter-hint {
      background: transparent;
      color: ${colors.textSecondary};
    }
    .bundle-meter-progress {
      background: ${colors.accentSoft};
      color: ${colors.accent};
    }
    .bundle-meter-progress .bundle-meter-text { font-weight: 600; }
    .bundle-meter-active {
      background: ${colors.accentSoft};
      color: ${colors.accent};
      animation: meter-pop 0.3s ease-out;
    }
    .bundle-meter-active .bundle-meter-text { font-weight: 700; }
    .bundle-meter-active .bundle-meter-text::before { content: '✓ '; }
    @keyframes meter-pop {
      from { transform: scale(0.96); opacity: 0.7; }
      to   { transform: scale(1);    opacity: 1; }
    }
    .bundle-dots {
      display: inline-flex;
      gap: 4px;
      align-items: center;
    }
    .bundle-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${colors.border};
      transition: background 0.25s ease, transform 0.25s ease;
    }
    .bundle-dot.filled {
      background: ${colors.accent};
      transform: scale(1.15);
    }

    .consent-inline {
      font-size: 12px;
      color: ${colors.textTertiary};
      line-height: 1.45;
      margin-top: 2px;
      text-align: center;
    }
    .consent-inline a {
      color: ${colors.textSecondary};
      text-decoration: underline;
    }
    .consent-inline a:hover { color: ${colors.accent}; }

    .consent-wrapper { display: flex; flex-direction: column; gap: 10px; }
    .consent-label { display: flex; gap: 10px; align-items: flex-start; cursor: pointer; }
    .consent-label input[type="checkbox"] {
      margin-top: 2px;
      cursor: pointer;
      width: 18px; height: 18px;
      accent-color: ${colors.accent};
      flex-shrink: 0;
    }
    .consent-text { font-size: 13px; color: ${colors.textSecondary}; line-height: 1.4; }
    .consent-text a { color: ${colors.accent}; }

    .thankyou-cta {
      display: flex;
      flex-direction: column;
      gap: 4px;
      background: ${colors.bgSubtle};
      border: 1px solid ${colors.border};
      border-radius: ${radiusMd};
      padding: 14px 16px;
      text-decoration: none;
      color: ${colors.text};
      transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
    }
    .thankyou-cta:hover {
      background: ${colors.bg};
      border-color: ${colors.accent};
      transform: translateY(-1px);
    }
    .thankyou-cta:active { transform: translateY(0); }
    .thankyou-cta-title {
      font-size: 15px;
      font-weight: 600;
      color: ${colors.accent};
      line-height: 1.3;
    }
    .thankyou-cta-sub {
      font-size: 13px;
      color: ${colors.textSecondary};
      line-height: 1.4;
    }

    .resume-overlay {
      position: absolute; inset: 0;
      background: rgba(0,0,0,0.45);
      display: flex; align-items: center; justify-content: center;
      padding: 24px;
      z-index: 50;
      animation: msgIn 0.2s ease;
    }
    .resume-card {
      background: ${colors.bg};
      color: ${colors.text};
      border-radius: ${radiusLg};
      padding: 24px;
      max-width: 320px;
      width: 100%;
      ${isDark ? 'border: 1px solid ' + colors.border + ';' : ''}
      box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    }
    .resume-card h3 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
    .resume-card p { font-size: 14px; color: ${colors.textSecondary}; margin-bottom: 18px; line-height: 1.5; }
    .resume-actions { display: flex; gap: 8px; }
    .resume-actions .btn-send { flex: 1; padding: 12px 14px; font-size: 14px; }
    .resume-actions .btn-secondary {
      background: transparent;
      color: ${colors.textSecondary};
      border: 1.5px solid ${colors.border};
    }
    .resume-actions .btn-secondary:hover {
      background: ${colors.bgSubtle};
      border-color: ${colors.textSecondary};
      color: ${colors.text};
    }
  `
}
